import { Component, inject } from '@angular/core';
import { DroneFlightService } from '../proxy/inventory/controllers';
import { ListService } from '@abp/ng.core';
import {
  CardComponent,
  CardBodyComponent,
  ConfirmationService,
} from '@abp/ng.theme.shared';
import { DroneFlightCreateDto, DroneFlightUpdateDto } from '../proxy/inventory/dtos/drone-flight';
import { DRONEFLIGHT_CONFIG, DRONEFLIGHT_FIELDS } from './drone-flight.config';
import { BaseCrudComponent, CrudPage, GenericForm, ICrudService } from '@zendra/shared';
import { DroneFlightProjectionPlanningOrchestratorService } from 'orchestrator';
import {
  DroneFlightProjectionDto,
  DroneFlightProjectionExcelDownloadDto,
  GetDroneFlightProjectionListInputDto,
} from 'projects/orchestrator/src/lib/proxy/upm/zendra/cms/projection/drone-flights/models';

@Component({
  selector: 'app-drone-flight',
  imports: [
    CardComponent,
    CardBodyComponent,
    CrudPage,
    GenericForm,
  ],
  providers: [ListService, DroneFlightService, ConfirmationService],
  templateUrl: './drone-flight.html',
  styleUrl: './drone-flight.scss',
})
export class DroneFlight extends BaseCrudComponent<
  DroneFlightProjectionDto,
  DroneFlightCreateDto,
  DroneFlightUpdateDto,
  DroneFlightProjectionExcelDownloadDto
> {
  private readonly inventoryService = inject(DroneFlightService);
  private readonly orchestratorService = inject(DroneFlightProjectionPlanningOrchestratorService);

  service: ICrudService<
    DroneFlightProjectionDto,
    DroneFlightCreateDto,
    DroneFlightUpdateDto,
    DroneFlightProjectionExcelDownloadDto
  > = {
    create: input => this.inventoryService.create(input),
    update: (id, input) => this.inventoryService.update(id, input),
    deleteByIds: ids => this.inventoryService.deleteByIds(ids),
    getListByIds: input => this.inventoryService.getListByIds(input),
    getList: (query: GetDroneFlightProjectionListInputDto) =>
      this.orchestratorService.getList(query),
    getDownloadToken: () => this.orchestratorService.getDownloadToken(),
    getListAsExcelFile: input => this.orchestratorService.getListAsExcelFile(input),
    projectionConsistency: this.createProjectionConsistency(
      this.orchestratorService,
      'droneFlightId',
    ),
  };
  config = DRONEFLIGHT_CONFIG;
  fields = DRONEFLIGHT_FIELDS;

  protected prepareUpdateDto(
    data: any,
    selected: DroneFlightProjectionDto,
  ): DroneFlightUpdateDto {
    return {
      area: data.area,
      flightDate: data.flightDate,
      treeCount: data.treeCount,
      realDensity: data.realDensity,
      theoreticalDensity: data.theoreticalDensity,
      survivalPercentage: data.survivalPercentage,
      isLastInventory: data.isLastInventory,
      silvicultureVendorId: data.silvicultureVendorId,
      flightVendorId: data.flightVendorId,
      countingVendorId: data.countingVendorId,
      rotationId: data.rotationId ?? selected.rotationId,
      spacingId: data.spacingId,
      periodId: data.periodId,
      geneticMaterialId: data.geneticMaterialId,
      inventoryTypeId: data.inventoryTypeId,
      propertyId: data.propertyId ?? selected.propertyId,
      standCycle: data.standCycle ?? data.cycleValue ?? selected.cycleValue,
      standId: data.standId ?? selected.standId,
      standRotation: data.standRotation ?? data.rotationValue ?? selected.rotationValue,
      concurrencyStamp: selected.concurrencyStamp,
    };
  }

  protected getEntityName(): string {
    return 'DroneFlight';
  }
}
